import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Activity,
  FileText,
  Bell,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/useSubscription';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const NAV = [
  { to: '/', label: 'Radar', icon: LayoutDashboard },
  { to: '/indicators', label: 'Signals', icon: Activity },
  { to: '/brief', label: 'Brief', icon: FileText },
  { to: '/alerts', label: 'Alerts', icon: Bell },
  { to: '/admin', label: 'Sources', icon: ShieldCheck },
];

/** Whether a nav item is active for the current path (sections match sub-routes). */
function isNavActive(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function BchLogo({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg bg-secondary shadow-[0_0_20px_hsl(145_63%_49%/0.35)] ring-1 ring-primary/40',
        className,
      )}
    >
      <svg viewBox="0 0 64 64" className="h-6 w-6" aria-hidden="true">
        <defs>
          <linearGradient id="hdrSweep" x1="32" y1="32" x2="58" y2="12" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="hsl(145 63% 49%)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(145 63% 49%)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M32 32 L58 12 A32 32 0 0 1 58 32 Z" fill="url(#hdrSweep)" />
        <g fill="none" stroke="hsl(145 63% 49%)" strokeOpacity="0.4" strokeWidth="2.5">
          <circle cx="32" cy="32" r="24" />
          <circle cx="32" cy="32" r="15" />
        </g>
        <line x1="32" y1="32" x2="58" y2="12" stroke="hsl(145 63% 49%)" strokeWidth="2.5" strokeOpacity="0.85" />
        <circle cx="32" cy="32" r="5" fill="hsl(145 63% 49%)" />
      </svg>
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isPro } = useSubscription();

  return (
    <div className="min-h-screen bg-terminal text-foreground flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2.5">
            <BchLogo />
            <div className="leading-tight">
              <div className="font-semibold tracking-tight">
                BCH <span className="text-primary">Radar</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Know what&apos;s changing
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => {
              const active = isNavActive(item.to, location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    active
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {isPro ? (
              <Link to="/pricing">
                <Badge className="gap-1 bg-primary/15 text-primary border-primary/30">
                  <Zap className="h-3 w-3" /> PRO
                </Badge>
              </Link>
            ) : (
              <Button
                asChild
                size="sm"
                className="gap-1.5 font-semibold shadow-md shadow-primary/25 transition-transform hover:scale-[1.03]"
              >
                <Link to="/pricing">
                  <Zap className="h-4 w-4" /> Upgrade
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-24 pt-5 md:pb-10">
        {children}

        {/* Mobile disclaimer + attribution (desktop uses the footer below). */}
        <p className="mt-8 text-center text-[11px] leading-relaxed text-muted-foreground md:hidden">
          Not financial advice · Signals describe conditions, not predictions
          <br />
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            Vibed with Shakespeare
          </a>
        </p>
      </main>

      {/* Footer */}
      <footer className="hidden border-t border-border/60 py-6 text-center text-xs text-muted-foreground md:block">
        <p>
          Not financial advice · Signals describe conditions, not predictions ·{' '}
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            Vibed with Shakespeare
          </a>
        </p>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/90 backdrop-blur-xl md:hidden">
        <div className="mx-auto grid max-w-6xl grid-cols-6">
          {NAV.map((item) => {
            const active = isNavActive(item.to, location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
